/**
 * 🎬 ANIMATED ELEMENT - Framer Motion Wrapper
 * 
 * Universal wrapper component für Scroll-Triggered Animationen
 * Ersetzt alte CSS-basierte Animationen mit Framer Motion
 * 
 * Features:
 * - Automatische Scroll-Detection via whileInView
 * - Performance-optimiert (nur im Live Mode)
 * - Wiederverwendbar für alle Element-Typen
 * - Smooth, elegant animations
 * 
 * Usage:
 * <AnimatedElement 
 *   animationEnabled={true}
 *   animationType="fadeInUp"
 *   animationSpeed="normal"
 *   isEditMode={false}
 * >
 *   <YourContent />
 * </AnimatedElement>
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
    getVariant, 
    withCustomDuration, 
    viewportConfig,
    viewportConfigImages
} from '../../utils/animationVariants';

function AnimatedElement({
    children,
    animationEnabled = false,
    animationType = 'fadeInUp',
    animationSpeed = 'normal',
    isEditMode = false,
    triggerAnimation = null, // ✅ Manual trigger für Preview im Edit-Mode
    isImage = false, // ✅ Images brauchen mehr Lead-Time
    className = '',
    style = {},
    // ✅ Additional motion props
    ...motionProps
}) {
    // 🎯 Im Edit-Mode: Keine Animationen (außer Manual Preview)
    // Im Live-Mode: Scroll-Triggered Animationen
    
    // ❌ Disabled: Render als normale Div
    if (!animationEnabled) {
        return (
            <div className={className} style={style}>
                {children}
            </div>
        );
    }
    
    // ✅ Get animation variant
    const variant = getVariant(animationType);
    const variantWithSpeed = withCustomDuration(variant, animationSpeed);
    
    // 🎯 EDIT MODE: Keine Scroll-Animation, nur Manual Preview
    if (isEditMode) {
        return (
            <motion.div
                key={triggerAnimation} // ✅ Trigger Animation on change
                initial="hidden"
                animate="visible"
                variants={variantWithSpeed}
                className={className}
                style={style}
                {...motionProps}
            >
                {children}
            </motion.div>
        );
    }
    
    // 🎯 LIVE MODE: Scroll-Triggered Animation
    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={isImage ? viewportConfigImages : viewportConfig}
            variants={variantWithSpeed}
            className={className}
            style={style}
            {...motionProps}
        >
            {children}
        </motion.div>
    );
}

export default AnimatedElement;

